/**
 * Language Context and Provider
 *
 * Provides language state and translations to all child components.
 * Implements automatic language detection and localStorage persistence.
 */

import React, { createContext, useState, useMemo, useCallback } from 'react';

// Import locale files
import ruLocale from './locales/ru.json';
import enLocale from './locales/en.json';

/**
 * @typedef {'ru' | 'en'} Language
 */

/**
 * @typedef {Object} LanguageContextValue
 * @property {Language} language - Current language code
 * @property {(lang: Language) => void} setLanguage - Change language
 * @property {Object} translations - Translation object for current language
 */

const LOCALES = {
  ru: ruLocale,
  en: enLocale
};

const STORAGE_KEY = 'miniapp_lang';
const DEFAULT_LANGUAGE = 'en';
const SUPPORTED_LANGUAGES = ['ru', 'en'];

/**
 * Detect user language with priority:
 * 1. localStorage (user explicit choice)
 * 2. Telegram.WebApp.initDataUnsafe.user.language_code
 * 3. navigator.language
 * 4. Default: 'en'
 *
 * @returns {Language}
 */
function detectLanguage() {
  // 1. Check localStorage (user preference)
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
      return stored;
    }
  } catch (e) {
    // localStorage not available
  }

  // 2. Check Telegram WebApp language
  try {
    const tgLang = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
    if (tgLang) {
      // Telegram returns 'en', 'ru', 'uk', etc.
      const lang = tgLang.substring(0, 2).toLowerCase();
      if (SUPPORTED_LANGUAGES.includes(lang)) {
        return lang;
      }
    }
  } catch (e) {
    // Telegram WebApp not available
  }

  // 3. Check browser language
  try {
    const browserLang = navigator.language?.substring(0, 2).toLowerCase();
    if (SUPPORTED_LANGUAGES.includes(browserLang)) {
      return browserLang;
    }
  } catch (e) {
    // navigator not available
  }

  // 4. Default
  return DEFAULT_LANGUAGE;
}

/** @type {React.Context<LanguageContextValue|null>} */
export const LanguageContext = createContext(null);

/**
 * Language Provider Component
 *
 * Wrap your app with this to enable translations.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => detectLanguage());

  // Persist language choice to localStorage (memoized to prevent re-renders)
  const setLanguage = useCallback((lang) => {
    if (!SUPPORTED_LANGUAGES.includes(lang)) {
      console.warn(`[i18n] Unsupported language: ${lang}`);
      return;
    }

    setLanguageState(lang);

    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch (e) {
      // localStorage not available
    }
  }, []);

  // Get translations for current language with ru fallback
  const translations = useMemo(() => {
    return LOCALES[language] || LOCALES[DEFAULT_LANGUAGE];
  }, [language]);

  // Context value
  const value = useMemo(() => ({
    language,
    setLanguage,
    translations
  }), [language, setLanguage, translations]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}
